import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Checkbox,
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    Link,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
    Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Must match ZOFIQ_PROVISION_API_KEY in lambda_function_prov.py
const ZOFIQ_PROVISION_X_API_KEY = 'rOHFh0-Isu1tAMG5XHlLFRUUd-5n_pGYcuu-yIaR5fOp9fQ0y5PXODF02c43qzfp';

const PIPELINE_TYPES = [
    { value: 'Onboarding', label: 'Onboarding' },
    { value: 'Migration', label: 'Migration' },
];

const PRODUCT_OPTIONS = [
    { value: 'ai-triage', label: 'AI Triage' },
    { value: 'ai-voice', label: 'AI Voice' },
    { value: 'ai-teams', label: 'AI Teams' },
];

function ZofiQProvisionScreen() {
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_FLASK_API_URL_PROD || '';
    // update-instance-type uses the same API URL as partnerDetails.js
    const provisionApiUrl = process.env.REACT_APP_API_URL_PROD || '';
    const [version] = useState('v5.1');

    // ── Search ────────────────────────────────────────────────────────────────
    const [searchPartnerId, setSearchPartnerId] = useState('');
    const [searchCompanyId, setSearchCompanyId] = useState('');
    const [searchInstanceId, setSearchInstanceId] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);

    // ── Inline edit (installTypeId) ────────────────────────────────────────────
    const [editableRowId, setEditableRowId] = useState(null);
    const [editableData, setEditableData] = useState({});
    const [previousInstallTypeId, setPreviousInstallTypeId] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);

    // ── Provision modal ───────────────────────────────────────────────────────
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [pipelineType, setPipelineType] = useState('Onboarding');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalResult, setModalResult] = useState(null); // { success, title, message }

    // ── Instance details modal (Security Roles + API Members) ─────────────────
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsRow, setDetailsRow] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [securityRoles, setSecurityRoles] = useState([]);
    const [apiMembers, setApiMembers] = useState([]);

    // ── Remove ZofiQ modal ────────────────────────────────────────────────────
    const [removeOpen, setRemoveOpen] = useState(false);
    const [removeRow, setRemoveRow] = useState(null);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [removeResult, setRemoveResult] = useState(null); // { success, title, message }

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleLogout = () => {
        fetch(apiUrl + '/api/provision/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'sso-token': sessionStorage.getItem('ssoToken'),
            },
        })
            .catch((err) => console.error('Error logging out:', err))
            .finally(() => {
                sessionStorage.removeItem('isAuthenticated');
                sessionStorage.removeItem('ssoToken');
                navigate('/login');
            });
    };

    const handleSearch = (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!searchPartnerId && !searchCompanyId && !searchInstanceId) {
            setSearchError('At least one of Partner ID, Company ID, or Instance ID is required.');
            return;
        }

        setSearchError(null);
        setSearchLoading(true);
        setSearchResults(null);

        fetch(apiUrl + '/api/provision/getPartner', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                partnerId: searchPartnerId,
                companyId: searchCompanyId,
                instanceId: searchInstanceId,
            }),
        })
            .then((res) => res.json())
            .then((data) => {
                setSearchResults(data);
                setSearchLoading(false);
            })
            .catch((err) => {
                console.error('Error in getPartner fetch:', err);
                setSearchError('Search failed. Please try again.');
                setSearchLoading(false);
            });
    };

    // Inline edit
    const handleEditClick = (row) => {
        // Store raw value (may be undefined) — matches partnerDetails.js which also
        // sends the raw value so JSON.stringify converts undefined → null, letting
        // the backend choose "add" vs "replace" correctly.
        setPreviousInstallTypeId(row.metadata.installTypeId);
        setEditableRowId(row.id);
        setEditableData({ ...row, metadata: { ...row.metadata, installTypeId: row.metadata.installTypeId || '' } });
    };

    const handleEditInputChange = (e) => {
        const value = e.target.value.toUpperCase().slice(0, 1);
        setEditableData((prev) => ({ ...prev, metadata: { ...prev.metadata, installTypeId: value } }));
    };

    const handleEditCancel = () => {
        setEditableRowId(null);
        setEditableData({});
        setEditError(null);
    };

    const handleEditSave = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const newValue = editableData.metadata.installTypeId;
        setEditLoading(true);
        setEditError(null);

        fetch(provisionApiUrl + '/api/provision/update-instance-type', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            body: JSON.stringify({ productInstanceId: editableData.id, oldValue: previousInstallTypeId, newValue: newValue }, null, 2),
        })
            .then(async (res) => {
                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    throw new Error(body || `HTTP ${res.status}`);
                }
                return res.json();
            })
            .then(() => {
                setEditLoading(false);
                setEditableRowId(null);
                handleSearch({ preventDefault: () => {} });
            })
            .catch((err) => {
                console.error('Error updating instance type:', err);
                setEditLoading(false);
                setEditError(err.message || 'Failed to update type.');
            });
    };

    // Provision modal
    const handleOpenModal = (row) => {
        setSelectedRow(row);
        setPipelineType('Onboarding');
        setSelectedProducts([]);
        setModalResult(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedRow(null);
        setModalResult(null);
    };

    // Instance details modal
    const handleOpenDetailsModal = (row) => {
        setDetailsRow(row);
        setDetailsOpen(true);
        setDetailsError(null);
        setSecurityRoles([]);
        setApiMembers([]);
        setDetailsLoading(true);

        fetch(apiUrl + '/api/provision/zofiQ-instance-details', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': ZOFIQ_PROVISION_X_API_KEY,
            },
            body: JSON.stringify({
                productInstanceId: row.id,
                companyId: String(row.instanceIdentifier),
            }),
        })
            .then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    const msg = data?.error || data?.message || `HTTP error ${res.status}`;
                    throw new Error(msg);
                }
                return data;
            })
            .then((data) => {
                setSecurityRoles(Array.isArray(data.securityRoles) ? data.securityRoles : []);
                setApiMembers(Array.isArray(data.apiMembers) ? data.apiMembers : []);
                setDetailsLoading(false);
            })
            .catch((err) => {
                console.error('ZofiQ instance details error:', err);
                setDetailsError(err.message || 'Failed to load instance details.');
                setDetailsLoading(false);
            });
    };

    const handleCloseDetailsModal = () => {
        setDetailsOpen(false);
        setDetailsRow(null);
        setDetailsError(null);
        setSecurityRoles([]);
        setApiMembers([]);
    };

    const formatExcludedBoardIds = (value) => {
        if (value == null) return '—';
        if (Array.isArray(value)) {
            if (value.length === 0) return '—';
            return value
                .map((item) => (item && typeof item === 'object' ? (item.id ?? JSON.stringify(item)) : String(item)))
                .join(', ');
        }
        return String(value);
    };

    // Remove ZofiQ
    const handleOpenRemoveModal = (row) => {
        setRemoveRow(row);
        setRemoveResult(null);
        setRemoveOpen(true);
    };

    const handleCloseRemoveModal = () => {
        if (removeLoading) return;
        setRemoveOpen(false);
        setRemoveRow(null);
        setRemoveResult(null);
    };

    const handleRemoveSubmit = () => {
        if (!removeRow) return;
        const connectwiseUrl = removeRow.metadata?.domain || '';

        setRemoveLoading(true);
        setRemoveResult(null);
        fetch(apiUrl + '/api/provision/zofiQ-remove', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': ZOFIQ_PROVISION_X_API_KEY,
            },
            body: JSON.stringify({
                productInstanceId: removeRow.id,
                companyId: String(removeRow.instanceIdentifier),
                connectwiseUrl,
            }),
        })
            .then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    const detail = Array.isArray(data?.details) ? data.details.join('\n') : '';
                    const msg = data?.error || data?.message || detail || `HTTP error ${res.status}`;
                    throw new Error(msg);
                }
                return data;
            })
            .then((data) => {
                setRemoveLoading(false);
                const details = Array.isArray(data?.details) ? data.details.join('\n') : '';
                setRemoveResult({
                    success: true,
                    title: 'Removed',
                    message: details || (typeof data === 'string' ? data : JSON.stringify(data, null, 2)),
                });
            })
            .catch((err) => {
                console.error('ZofiQ remove error:', err);
                setRemoveLoading(false);
                setRemoveResult({
                    success: false,
                    title: 'Error',
                    message: err.message || 'Failed to remove ZofiQ provisioning.',
                });
            });
    };

    const handleProvisionSubmit = () => {
        if (!selectedProducts.length) return;
        const connectwiseUrl = selectedRow.metadata.domain || '';

        setModalLoading(true);
        fetch(apiUrl + '/api/provision/zofiQ', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': ZOFIQ_PROVISION_X_API_KEY,
            },
            body: JSON.stringify({
                productInstanceId: selectedRow.id,
                companyId: String(selectedRow.instanceIdentifier),
                connectwiseUrl,
                pipelineType,
                products: selectedProducts,
            }),
        })
            .then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    const msg = data?.error || data?.message || `HTTP error ${res.status}`;
                    throw new Error(msg);
                }
                return data;
            })
            .then((data) => {
                setModalLoading(false);
                setModalResult({
                    success: true,
                    title: 'Success',
                    message: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
                });
            })
            .catch((err) => {
                console.error('ZofiQ provision error:', err);
                setModalLoading(false);
                setModalResult({
                    success: false,
                    title: 'Error',
                    message: err.message || 'Failed to provision ZofiQ.',
                });
            });
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ width: '95%', maxWidth: 1400, mt: 2, mb: 4 }}>
                <Card elevation={3}>
                    <CardContent sx={{ p: { xs: 2, sm: 4 } }}>

                        {/* Header */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                            <Typography variant="h5" fontWeight={600}>ZofiQ Provisioning</Typography>
                            <Button size="small" variant="text" onClick={() => navigate('/provision')}>
                                ← Back to PSA Provisioning
                            </Button>
                        </Stack>

                        {/* Search form */}
                        <Box component="form" onSubmit={handleSearch} mt={2}>
                            <Stack direction="row" flexWrap="wrap" gap={2} alignItems="flex-start">
                                <TextField
                                    label="Partner ID"
                                    variant="outlined"
                                    size="small"
                                    value={searchPartnerId}
                                    onChange={(e) => setSearchPartnerId(e.target.value)}
                                    sx={{ minWidth: 260 }}
                                />
                                <TextField
                                    label="Company ID"
                                    variant="outlined"
                                    size="small"
                                    value={searchCompanyId}
                                    onChange={(e) => setSearchCompanyId(e.target.value)}
                                    sx={{ minWidth: 160 }}
                                />
                                <TextField
                                    label="Instance ID"
                                    variant="outlined"
                                    size="small"
                                    value={searchInstanceId}
                                    onChange={(e) => setSearchInstanceId(e.target.value)}
                                    sx={{ minWidth: 260 }}
                                />
                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={searchLoading}
                                    sx={{ height: 40, alignSelf: 'center' }}
                                >
                                    {searchLoading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                                </Button>
                            </Stack>
                            {searchError && (
                                <Alert severity="error" sx={{ mt: 2 }}>{searchError}</Alert>
                            )}
                        </Box>

                        {/* Inline edit error */}
                        {editError && (
                            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setEditError(null)}>
                                {editError}
                            </Alert>
                        )}

                        {/* Results table */}
                        {searchResults && (
                            <Box sx={{ overflowX: 'auto', mt: 3 }}>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small" aria-label="ZofiQ partner results">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                                <TableCell><strong>Partner ID</strong></TableCell>
                                                <TableCell><strong>Instance ID</strong></TableCell>
                                                <TableCell><strong>Product</strong></TableCell>
                                                <TableCell><strong>Company ID</strong></TableCell>
                                                <TableCell><strong>Status</strong></TableCell>
                                                <TableCell><strong>Domain</strong></TableCell>
                                                <TableCell><strong>Company Name</strong></TableCell>
                                                <TableCell><strong>Type</strong></TableCell>
                                                <TableCell><strong>Actions</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Array.isArray(searchResults) && searchResults.map((row) => (
                                                <TableRow key={row.id} hover>
                                                    <TableCell>{row.partnerId}</TableCell>
                                                    <TableCell>
                                                        {row.metadata?.installTypeId === 'P' ? (
                                                            <Link
                                                                component="button"
                                                                type="button"
                                                                variant="body2"
                                                                onClick={() => handleOpenDetailsModal(row)}
                                                                sx={{ textAlign: 'left', cursor: 'pointer' }}
                                                            >
                                                                {row.id}
                                                            </Link>
                                                        ) : (
                                                            row.id
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{row.product?.identifier ?? '—'}</TableCell>
                                                    <TableCell>{row.instanceIdentifier}</TableCell>
                                                    <TableCell>{row.instanceStatus?.identifier ?? '—'}</TableCell>
                                                    <TableCell>{row.metadata?.domain ?? '—'}</TableCell>
                                                    <TableCell>{row.metadata?.loginCompanyName ?? '—'}</TableCell>
                                                    <TableCell>
                                                        {editableRowId === row.id ? (
                                                            <TextField
                                                                value={editableData.metadata?.installTypeId ?? ''}
                                                                onChange={handleEditInputChange}
                                                                size="small"
                                                                inputProps={{ maxLength: 1, style: { width: 36, textAlign: 'center' } }}
                                                            />
                                                        ) : (
                                                            row.metadata?.installTypeId || '—'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {editableRowId === row.id ? (
                                                            <Stack direction="row" gap={1} component="form" onSubmit={handleEditSave}>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    type="submit"
                                                                    disabled={editLoading}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={handleEditCancel}
                                                                    disabled={editLoading}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </Stack>
                                                        ) : (
                                                            <Stack direction="row" gap={1}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                {row.metadata?.installTypeId === 'P' && (
                                                                    <>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => handleOpenModal(row)}
                                                                        >
                                                                            Provision
                                                                        </Button>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleOpenRemoveModal(row)}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                    </CardContent>
                </Card>

                {/* Footer */}
                <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={1} gap={1}>
                    <Typography variant="caption" color="text.secondary">@2026 ConnectWise ({version})</Typography>
                    <Button size="small" variant="outlined" onClick={handleLogout}>Logout</Button>
                </Stack>
            </Box>

            {/* Provision ZofiQ modal */}
            <Dialog
                open={modalOpen}
                onClose={modalResult ? handleCloseModal : undefined}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {modalResult ? modalResult.title : 'Provision ZofiQ'}
                </DialogTitle>

                <DialogContent dividers>
                    {modalLoading && (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                Running provisioning pipeline…
                            </Typography>
                        </Stack>
                    )}

                    {!modalLoading && modalResult && (
                        <Alert severity={modalResult.success ? 'success' : 'error'} sx={{ whiteSpace: 'pre-wrap' }}>
                            {modalResult.message}
                        </Alert>
                    )}

                    {!modalLoading && !modalResult && selectedRow && (
                        <Stack gap={2} mt={1}>
                            {/* Read-only context */}
                            <Stack direction="row" flexWrap="wrap" gap={2}>
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                    <Typography variant="caption" color="text.secondary">Company ID</Typography>
                                    <Typography variant="body1" fontWeight={500}>{selectedRow.instanceIdentifier}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                    <Typography variant="caption" color="text.secondary">Domain</Typography>
                                    <Typography variant="body1" fontWeight={500}>{selectedRow.metadata?.domain ?? '—'}</Typography>
                                </Box>
                            </Stack>

                            {/* User inputs */}
                            <FormControl fullWidth>
                                <InputLabel id="modal-pipeline-type-label">Pipeline Type</InputLabel>
                                <Select
                                    labelId="modal-pipeline-type-label"
                                    label="Pipeline Type"
                                    value={pipelineType}
                                    onChange={(e) => setPipelineType(e.target.value)}
                                >
                                    {PIPELINE_TYPES.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth required error={!selectedProducts.length}>
                                <InputLabel id="modal-products-label">Products</InputLabel>
                                <Select
                                    labelId="modal-products-label"
                                    label="Products"
                                    multiple
                                    value={selectedProducts}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSelectedProducts(typeof value === 'string' ? value.split(',') : value);
                                    }}
                                    input={<OutlinedInput label="Products" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const opt = PRODUCT_OPTIONS.find((p) => p.value === value);
                                                return <Chip key={value} label={opt?.label ?? value} size="small" />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {PRODUCT_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Checkbox checked={selectedProducts.indexOf(opt.value) > -1} />
                                            <ListItemText primary={opt.label} />
                                        </MenuItem>
                                    ))}
                                </Select>
                                {!selectedProducts.length && (
                                    <FormHelperText>Select at least one product.</FormHelperText>
                                )}
                            </FormControl>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    {modalResult ? (
                        <Button onClick={handleCloseModal} variant="contained">Close</Button>
                    ) : (
                        <>
                            <Button onClick={handleCloseModal} disabled={modalLoading}>Cancel</Button>
                            <Button
                                onClick={handleProvisionSubmit}
                                variant="contained"
                                disabled={modalLoading || !selectedProducts.length}
                            >
                                Provision ZofiQ
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Instance details modal — Security Roles + API Members */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetailsModal}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    Instance Details
                    {detailsRow && (
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
                            Instance ID: {detailsRow.id} · Company ID: {detailsRow.instanceIdentifier} · Domain: {detailsRow.metadata?.domain ?? '—'}
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    {detailsLoading && (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                Loading security roles and API members…
                            </Typography>
                        </Stack>
                    )}

                    {!detailsLoading && detailsError && (
                        <Alert severity="error">{detailsError}</Alert>
                    )}

                    {!detailsLoading && !detailsError && (
                        <Stack gap={3}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                                    Security Roles
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small" aria-label="Security roles">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                                <TableCell><strong>id</strong></TableCell>
                                                <TableCell><strong>name</strong></TableCell>
                                                <TableCell><strong>roleType</strong></TableCell>
                                                <TableCell><strong>inactiveFlag</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {securityRoles.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4}>
                                                        <Typography variant="body2" color="text.secondary">No security roles found.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                securityRoles.map((role) => (
                                                    <TableRow key={role.id ?? role.name} hover>
                                                        <TableCell>{role.id ?? '—'}</TableCell>
                                                        <TableCell>{role.name ?? '—'}</TableCell>
                                                        <TableCell>{role.roleType ?? '—'}</TableCell>
                                                        <TableCell>{String(role.inactiveFlag ?? '—')}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                                    Member Id
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small" aria-label="API members">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                                <TableCell><strong>id</strong></TableCell>
                                                <TableCell><strong>identifier</strong></TableCell>
                                                <TableCell><strong>name</strong></TableCell>
                                                <TableCell><strong>inactiveFlag</strong></TableCell>
                                                <TableCell><strong>securityRole id</strong></TableCell>
                                                <TableCell><strong>securityRole name</strong></TableCell>
                                                <TableCell><strong>excludedServiceBoardIds</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {apiMembers.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7}>
                                                        <Typography variant="body2" color="text.secondary">No API members found.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                apiMembers.map((member) => (
                                                    <TableRow key={member.id ?? member.identifier} hover>
                                                        <TableCell>{member.id ?? '—'}</TableCell>
                                                        <TableCell>{member.identifier ?? '—'}</TableCell>
                                                        <TableCell>{member.name ?? '—'}</TableCell>
                                                        <TableCell>{String(member.inactiveFlag ?? '—')}</TableCell>
                                                        <TableCell>{member.securityRole?.id ?? '—'}</TableCell>
                                                        <TableCell>{member.securityRole?.name ?? '—'}</TableCell>
                                                        <TableCell>{formatExcludedBoardIds(member.excludedServiceBoardIds)}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDetailsModal} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Remove ZofiQ confirmation modal */}
            <Dialog
                open={removeOpen}
                onClose={removeResult || !removeLoading ? handleCloseRemoveModal : undefined}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {removeResult ? removeResult.title : 'Remove ZofiQ Provisioning'}
                </DialogTitle>

                <DialogContent dividers>
                    {removeLoading && (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                Removing ZofiQ Pod and deactivating ZOFIQ-API member…
                            </Typography>
                        </Stack>
                    )}

                    {!removeLoading && removeResult && (
                        <Alert severity={removeResult.success ? 'success' : 'error'} sx={{ whiteSpace: 'pre-wrap' }}>
                            {removeResult.message}
                        </Alert>
                    )}

                    {!removeLoading && !removeResult && removeRow && (
                        <Stack gap={2} mt={1}>
                            <Alert severity="warning">
                                This will remove the <strong>CW ZOFIQ POD</strong> and deactivate the{' '}
                                <strong>ZOFIQ-API</strong> member for this instance. This cannot be undone from here.
                            </Alert>
                            <Stack direction="row" flexWrap="wrap" gap={2}>
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                    <Typography variant="caption" color="text.secondary">Instance ID</Typography>
                                    <Typography variant="body1" fontWeight={500}>{removeRow.id}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                    <Typography variant="caption" color="text.secondary">Company ID</Typography>
                                    <Typography variant="body1" fontWeight={500}>{removeRow.instanceIdentifier}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                    <Typography variant="caption" color="text.secondary">Domain</Typography>
                                    <Typography variant="body1" fontWeight={500}>{removeRow.metadata?.domain ?? '—'}</Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    {removeResult ? (
                        <Button onClick={handleCloseRemoveModal} variant="contained">Close</Button>
                    ) : (
                        <>
                            <Button onClick={handleCloseRemoveModal} disabled={removeLoading}>Cancel</Button>
                            <Button
                                onClick={handleRemoveSubmit}
                                variant="contained"
                                color="error"
                                disabled={removeLoading}
                            >
                                Confirm Remove
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ZofiQProvisionScreen;
